// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';

const API_BASE = '/coret-api';
const OVERLAY_STORAGE_KEY = 'coret.reactive.overlay.publisher.v1';
let overlayPublisherCache: StoredOverlayPublisher | null | undefined;

export interface ReactiveProfile {
	idle_image_url: string | null;
	speaking_image_url: string | null;
	updated_at: number | null;
}

export interface OverlayParticipant {
	user_id: string;
	display_name: string;
	avatar_url: string;
	speaking: boolean;
}

export interface StoredOverlayPublisher {
	token: string;
	overlayUrl: string;
}

export interface MusicQueueItem {
	id: string;
	title: string;
	status: 'resolving' | 'ready' | 'failed';
	duration: number | null;
	added_by: string;
	source_url: string;
	source_type: string;
	thumbnail: string | null;
	youtube_id: string | null;
	error: string | null;
	stream_url: string | null;
	priority: boolean;
}

export interface MusicQueueState {
	request_price: number;
	priority_price: number;
	call_prices: {reaction: number; theme: number; entry_sound: number};
	call_effects: Array<{id: string; kind: 'reaction' | 'entry_sound' | 'theme'; value: string; userId: string; createdAt: number}>;
	call_theme: {value: string; userId: string; expiresAt: number} | null;
	current_id: string | null;
	started_at: number | null;
	paused_at: number | null;
	is_paused: boolean;
	items: Array<MusicQueueItem>;
}

export type CoretCollectibleKind = 'card' | 'mascot' | 'trophy' | 'badge';
export type CoretCollectibleRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface CoretCollectible {
	id: string;
	name: string;
	description: string;
	kind: CoretCollectibleKind;
	rarity: CoretCollectibleRarity;
	asset_url: string | null;
	visible: boolean;
}

export interface CoretRewardChance {item_id: string; weight: number}
export interface CoretPackage {
	id: string;
	name: string;
	description: string;
	price: number;
	rewards: Array<CoretRewardChance>;
	visible: boolean;
}
export interface CoretRaffle {
	id: string;
	name: string;
	rewardItemId: string;
	entryPrice: number;
	maxEntriesPerUser: number;
	endsAt: number | null;
	visible: boolean;
	entry_count: number;
	my_entries: number;
	winnerId: string | null;
	drawnAt: number | null;
}
export interface CoretChannelFee {channel_id: string; price: number; mode: 'permanent' | 'per_entry'}
export interface CoretCommunityState {
	music_request_price: number;
	music_priority_price: number;
	social_prices: {compliment: number; friendship_card: number; message_highlight: number};
	call_prices: {reaction: number; theme: number; entry_sound: number};
	collectibles: Array<CoretCollectible>;
	owned_collectible_ids: Array<string>;
	packages: Array<CoretPackage>;
	raffles: Array<CoretRaffle>;
	channel_fees: Array<CoretChannelFee>;
	recent_activity: Array<Record<string, unknown>>;
}

export type CoretShopItemType = 'avatar_frame' | 'nameplate' | 'profile_frame' | 'profile_effect';
export type CoretShopAnimation =
	| 'none'
	| 'pulse'
	| 'shimmer'
	| 'orbit'
	| 'float'
	| 'glow'
	| 'bounce'
	| 'breathe'
	| 'swing'
	| 'spin'
	| 'zoom'
	| 'flicker'
	| 'wave'
	| 'slide_left'
	| 'slide_right'
	| 'slide_up'
	| 'slide_down'
	| 'fade'
	| 'rainbow'
	| 'hue'
	| 'gradient_left'
	| 'gradient_right'
	| 'blur'
	| 'sparkle'
	| 'heartbeat'
	| 'jitter'
	| 'tilt'
	| 'flip'
	| 'elastic'
	| 'smoke'
	| 'neon';
export type CoretShopFont = 'default' | 'serif' | 'monospace' | 'gothic' | 'rounded' | 'display';

export interface CoretShopItem {
	id: string;
	type: CoretShopItemType;
	name: string;
	description: string;
	price: number;
	accent: string;
	secondary: string;
	animation: CoretShopAnimation;
	font: CoretShopFont;
	asset_url: string | null;
	visible: boolean;
	created_at: number;
	updated_at: number;
}

export interface CoretProfileCosmetics {
	avatar_frame: CoretShopItem | null;
	nameplate: CoretShopItem | null;
	profile_frame: CoretShopItem | null;
	profile_effect: CoretShopItem | null;
	collectibles: Array<{id: string; name: string; kind: CoretCollectibleKind; rarity: CoretCollectibleRarity; asset_url: string | null}>;
	social_rewards: Array<{id: string; type: 'recognition' | 'compliment' | 'friendship_card'; message: string; from_user_id: string; created_at: number}>;
	social_activity: Array<{
		id: string;
		type: 'gift' | 'recognition' | 'compliment' | 'friendship_card';
		from_user_id: string;
		message: string;
		item_id: string | null;
		amount: number | null;
		created_at: number;
	}>;
}

export interface CoretRecognition {
	id: string;
	from_user_id: string;
	from_username: string;
	message: string;
	amount: number;
	received_amount: number;
	created_at: number;
	opened_at: number | null;
}

export interface CoretShopBanner {
	title: string;
	description: string;
	asset_url: string | null;
}

export interface CoretShopState {
	balance: number;
	call_seconds: number;
	seconds_until_next_coin: number;
	owned_item_ids: Array<string>;
	equipped: Record<CoretShopItemType, string | null>;
	is_admin: boolean;
	banner: CoretShopBanner;
	items: Array<CoretShopItem>;
	community: CoretCommunityState;
	awarded_item_id?: string;
}

export type CoretCallEconomyState = Pick<CoretShopState, 'balance' | 'call_seconds' | 'seconds_until_next_coin'>;

export interface CoretShopItemInput {
	type: CoretShopItemType;
	name: string;
	description: string;
	price: number;
	accent: string;
	secondary: string;
	animation: CoretShopAnimation;
	font: CoretShopFont;
	visible: boolean;
	asset_data?: string | null;
}

function getOverlayStorage(): Storage | null {
	if (typeof window === 'undefined') return null;
	try {
		return window.localStorage;
	} catch {
		return null;
	}
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
	const token = Authentication.authToken;
	if (!token) throw new Error('Sua sessão expirou. Entre novamente.');
	const controller = new AbortController();
	const abort = () => controller.abort(init.signal?.reason);
	if (init.signal?.aborted) abort();
	else init.signal?.addEventListener('abort', abort, {once: true});
	const timeout = window.setTimeout(() => controller.abort(), init.body ? 60_000 : 30_000);
	try {
		const response = await fetch(`${API_BASE}${path}`, {
			...init,
			headers: {
				...(init.body ? {'content-type': 'application/json'} : {}),
				...init.headers,
				Authorization: token,
			},
			cache: 'no-store',
			signal: controller.signal,
		});
		if (!response.ok) {
			const data = await response.json().catch(() => null);
			throw new Error(data?.message || `Falha na solicitação (${response.status})`);
		}
		if (response.status === 204) return undefined as T;
		return response.json() as Promise<T>;
	} catch (error) {
		if (controller.signal.aborted && !init.signal?.aborted) {
			throw new Error('A solicitação demorou demais. Tente novamente.');
		}
		throw error;
	} finally {
		window.clearTimeout(timeout);
		init.signal?.removeEventListener('abort', abort);
	}
}

export async function getReactiveProfile(): Promise<ReactiveProfile> {
	return request('/reactive/profile');
}

export async function getReactiveProfiles(userIds: ReadonlyArray<string>): Promise<Record<string, ReactiveProfile>> {
	const ids = [...new Set(userIds.filter((id) => /^\d+$/.test(id)))].slice(0, 100);
	if (ids.length === 0) return {};
	return request(`/reactive/profiles?user_ids=${encodeURIComponent(ids.join(','))}`);
}

export async function updateReactiveProfile(input: {
	idle_image_data?: string | null;
	speaking_image_data?: string | null;
}): Promise<ReactiveProfile> {
	return request('/reactive/profile', {method: 'PUT', body: JSON.stringify(input)});
}

export function readStoredOverlayPublisher(): StoredOverlayPublisher | null {
	if (overlayPublisherCache !== undefined) return overlayPublisherCache;
	try {
		const parsed = JSON.parse(getOverlayStorage()?.getItem(OVERLAY_STORAGE_KEY) || 'null');
		overlayPublisherCache =
			parsed?.token && parsed?.overlayUrl ? {token: parsed.token, overlayUrl: parsed.overlayUrl} : null;
	} catch {
		overlayPublisherCache = null;
	}
	return overlayPublisherCache;
}

function notifyOverlayPublisherChanged(): void {
	if (typeof window !== 'undefined') window.dispatchEvent(new Event('coret-overlay-publisher-changed'));
}

export async function createCallOverlay(): Promise<StoredOverlayPublisher> {
	const previous = readStoredOverlayPublisher();
	const response = await request<{token: string; overlay_url: string}>('/overlays', {
		method: 'POST',
		body: JSON.stringify({existing_token: previous?.token ?? null}),
	});
	const stored = {token: response.token, overlayUrl: response.overlay_url};
	overlayPublisherCache = stored;
	getOverlayStorage()?.setItem(OVERLAY_STORAGE_KEY, JSON.stringify(stored));
	notifyOverlayPublisherChanged();
	return stored;
}

export async function revokeCallOverlay(overlay = readStoredOverlayPublisher()): Promise<void> {
	if (!overlay) return;
	await request(`/overlays/${encodeURIComponent(overlay.token)}`, {method: 'DELETE'});
	const current = readStoredOverlayPublisher();
	if (current?.token === overlay.token) getOverlayStorage()?.removeItem(OVERLAY_STORAGE_KEY);
	overlayPublisherCache = null;
	notifyOverlayPublisherChanged();
}

export async function publishOverlayState(
	token: string,
	channelId: string | null,
	guildId: string | null,
	participants: Array<OverlayParticipant>,
): Promise<void> {
	return request(`/overlays/${encodeURIComponent(token)}/state`, {
		method: 'POST',
		body: JSON.stringify({channel_id: channelId, guild_id: guildId, participants}),
	});
}

export async function getMusicState(channelId: string): Promise<MusicQueueState> {
	return request(`/music/${encodeURIComponent(channelId)}`);
}

export async function subscribeMusicState(
	channelId: string,
	onState: (state: MusicQueueState) => void,
	signal: AbortSignal,
): Promise<void> {
	const token = Authentication.authToken;
	if (!token) throw new Error('Sua sessão expirou. Entre novamente.');
	const response = await fetch(`${API_BASE}/music/${encodeURIComponent(channelId)}/events`, {
		headers: {Authorization: token, Accept: 'text/event-stream'},
		cache: 'no-store',
		signal,
	});
	if (!response.ok || !response.body) throw new Error(`Falha na sincronização (${response.status})`);
	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	while (!signal.aborted) {
		const {done, value} = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, {stream: true}).replace(/\r\n/g, '\n');
		let boundary = buffer.indexOf('\n\n');
		while (boundary >= 0) {
			const event = buffer.slice(0, boundary);
			buffer = buffer.slice(boundary + 2);
			const payload = event
				.split('\n')
				.filter((line) => line.startsWith('data:'))
				.map((line) => line.slice(5).trimStart())
				.join('\n');
			if (payload) onState(JSON.parse(payload) as MusicQueueState);
			boundary = buffer.indexOf('\n\n');
		}
	}
}

export async function addMusic(channelId: string, url: string, priority = false): Promise<MusicQueueState> {
	return request(`/music/${encodeURIComponent(channelId)}/queue`, {method: 'POST', body: JSON.stringify({url, priority})});
}

export async function sendCoretCallEffect(
	channelId: string,
	kind: 'reaction' | 'entry_sound' | 'theme',
	value: string,
): Promise<MusicQueueState> {
	return request(`/music/${encodeURIComponent(channelId)}/effect`, {method: 'POST', body: JSON.stringify({kind, value})});
}

export async function skipMusic(channelId: string, itemId?: string): Promise<MusicQueueState> {
	return request(`/music/${encodeURIComponent(channelId)}/skip`, {
		method: 'POST',
		body: JSON.stringify({item_id: itemId}),
	});
}

export async function setMusicPaused(channelId: string, paused: boolean): Promise<MusicQueueState> {
	return request(`/music/${encodeURIComponent(channelId)}/pause`, {
		method: 'POST',
		body: JSON.stringify({paused}),
	});
}

export async function updateMusicDuration(
	channelId: string,
	itemId: string,
	duration: number,
): Promise<MusicQueueState> {
	return request(`/music/${encodeURIComponent(channelId)}/duration/${encodeURIComponent(itemId)}`, {
		method: 'POST',
		body: JSON.stringify({duration}),
	});
}

export async function removeMusic(channelId: string, itemId: string): Promise<MusicQueueState> {
	return request(`/music/${encodeURIComponent(channelId)}/queue/${encodeURIComponent(itemId)}`, {method: 'DELETE'});
}

export async function getCoretShop(): Promise<CoretShopState> {
	return request('/shop');
}

export async function publishCoretCallHeartbeat(channelId: string): Promise<CoretCallEconomyState> {
	return request('/economy/heartbeat', {method: 'POST', body: JSON.stringify({channel_id: channelId, compact: true})});
}

export async function purchaseCoretShopItem(itemId: string): Promise<CoretShopState> {
	return request(`/shop/items/${encodeURIComponent(itemId)}/purchase`, {method: 'POST', body: '{}'});
}

export async function equipCoretShopItem(type: CoretShopItemType, itemId: string | null): Promise<CoretShopState> {
	return request('/shop/equipped', {method: 'PUT', body: JSON.stringify({type, item_id: itemId})});
}

export async function getCoretProfileCosmetics(userId: string): Promise<CoretProfileCosmetics> {
	return request(`/shop/users/${encodeURIComponent(userId)}/cosmetics`);
}

export async function getCoretProfileCosmeticsBatch(
	userIds: ReadonlyArray<string>,
): Promise<Record<string, CoretProfileCosmetics>> {
	const ids = [...new Set(userIds.filter((id) => /^\d+$/.test(id)))].slice(0, 100);
	if (ids.length === 0) return {};
	return request(`/shop/users/cosmetics?user_ids=${encodeURIComponent(ids.join(','))}`);
}

export async function createCoretShopItem(input: CoretShopItemInput): Promise<CoretShopState> {
	return request('/shop/admin/items', {method: 'POST', body: JSON.stringify(input)});
}

export async function updateCoretShopItem(itemId: string, input: Partial<CoretShopItemInput>): Promise<CoretShopState> {
	return request(`/shop/admin/items/${encodeURIComponent(itemId)}`, {method: 'PATCH', body: JSON.stringify(input)});
}

export async function deleteCoretShopItem(itemId: string): Promise<CoretShopState> {
	return request(`/shop/admin/items/${encodeURIComponent(itemId)}`, {method: 'DELETE'});
}

export async function updateCoretShopBanner(input: {
	title: string;
	description: string;
	asset_data?: string | null;
}): Promise<CoretShopState> {
	return request('/shop/admin/banner', {method: 'PUT', body: JSON.stringify(input)});
}

export async function sendCoretGift(recipientId: string, itemId: string, message: string): Promise<CoretShopState> {
	return request('/community/gifts', {method: 'POST', body: JSON.stringify({recipient_id: recipientId, item_id: itemId, message})});
}

export async function sendCoretSocialReward(
	recipientId: string,
	amount: number,
	message: string,
): Promise<CoretShopState> {
	return request('/community/social', {method: 'POST', body: JSON.stringify({recipient_id: recipientId, amount, message})});
}

export async function getPendingCoretRecognitions(): Promise<Array<CoretRecognition>> {
	const response = await request<{recognitions: Array<CoretRecognition>}>('/community/recognitions/pending');
	return response.recognitions;
}

export async function openCoretRecognition(
	recognitionId: string,
): Promise<{recognition: CoretRecognition; shop: CoretShopState}> {
	return request(`/community/recognitions/${encodeURIComponent(recognitionId)}/open`, {method: 'POST', body: '{}'});
}

const highlightCache = new Map<string, {ids: Set<string>; expiresAt: number}>();
export async function getCoretChannelHighlights(channelId: string): Promise<Set<string>> {
	const cached = highlightCache.get(channelId);
	if (cached && cached.expiresAt > Date.now()) return cached.ids;
	const response = await request<{message_ids: Array<string>}>(`/community/channels/${encodeURIComponent(channelId)}/highlights`);
	const ids = new Set(response.message_ids);
	highlightCache.set(channelId, {ids, expiresAt: Date.now() + 30_000});
	return ids;
}

export async function highlightCoretMessage(channelId: string, messageId: string): Promise<CoretShopState> {
	const result = await request<CoretShopState>('/community/highlights', {method: 'POST', body: JSON.stringify({channel_id: channelId, message_id: messageId})});
	highlightCache.delete(channelId);
	window.dispatchEvent(new CustomEvent('coret-message-highlighted', {detail: {channelId, messageId}}));
	return result;
}

export async function openCoretPackage(packageId: string): Promise<CoretShopState> {
	return request(`/community/packages/${encodeURIComponent(packageId)}/open`, {method: 'POST', body: '{}'});
}

export async function enterCoretRaffle(raffleId: string): Promise<CoretShopState> {
	return request(`/community/raffles/${encodeURIComponent(raffleId)}/enter`, {method: 'POST', body: '{}'});
}

export async function createCoretCollectible(input: {
	name: string; description: string; kind: CoretCollectibleKind; rarity: CoretCollectibleRarity;
	visible: boolean; asset_data?: string | null;
}): Promise<CoretShopState> {
	return request('/community/admin/collectibles', {method: 'POST', body: JSON.stringify(input)});
}

export async function createCoretPackage(input: {
	name: string; description: string; price: number; visible: boolean; rewards: Array<CoretRewardChance>;
}): Promise<CoretShopState> {
	return request('/community/admin/packages', {method: 'POST', body: JSON.stringify(input)});
}

export async function createCoretRaffle(input: {
	name: string; reward_item_id: string; entry_price: number; max_entries_per_user: number; ends_at?: number | null;
}): Promise<CoretShopState> {
	return request('/community/admin/raffles', {method: 'POST', body: JSON.stringify(input)});
}

export async function drawCoretRaffle(raffleId: string): Promise<CoretShopState> {
	return request(`/community/admin/raffles/${encodeURIComponent(raffleId)}/draw`, {method: 'POST', body: '{}'});
}

export async function updateCoretEconomySettings(input: {
	music_request_price: number;
	music_priority_price: number;
	compliment_price?: number;
	friendship_card_price?: number;
	message_highlight_price?: number;
	call_reaction_price?: number;
	call_theme_price?: number;
	entry_sound_price?: number;
}): Promise<CoretShopState> {
	return request('/community/admin/settings', {method: 'PUT', body: JSON.stringify(input)});
}

export async function setCoretChannelFee(channelId: string, price: number, mode: 'permanent' | 'per_entry'): Promise<CoretShopState> {
	return request(`/community/admin/channel-fees/${encodeURIComponent(channelId)}`, {method: 'PUT', body: JSON.stringify({price, mode})});
}

export async function removeCoretChannelFee(channelId: string): Promise<CoretShopState> {
	return request(`/community/admin/channel-fees/${encodeURIComponent(channelId)}`, {method: 'DELETE'});
}

export interface CoretChannelAccess {requires_payment: boolean; fee: CoretChannelFee | null; balance: number}
export async function getCoretChannelAccess(channelId: string): Promise<CoretChannelAccess> {
	return request(`/community/channels/${encodeURIComponent(channelId)}/access`);
}
export async function purchaseCoretChannelAccess(channelId: string): Promise<CoretChannelAccess> {
	return request(`/community/channels/${encodeURIComponent(channelId)}/access`, {method: 'POST', body: '{}'});
}

export interface CoretContactNotificationSound {
	contact_id: string;
	file_name: string;
	updated_at: number | null;
}

interface CoretContactNotificationSoundsResponse {
	sounds: Array<CoretContactNotificationSound>;
}

let contactSoundOwnerId: string | null = null;
let contactSoundListPromise: Promise<Map<string, CoretContactNotificationSound>> | null = null;
const contactSoundAudioUrls = new Map<string, string>();

function clearContactSoundAudioUrl(contactId: string): void {
	const cachedUrl = contactSoundAudioUrls.get(contactId);
	if (cachedUrl) URL.revokeObjectURL(cachedUrl);
	contactSoundAudioUrls.delete(contactId);
}

function resetContactSoundCache(): void {
	for (const url of contactSoundAudioUrls.values()) URL.revokeObjectURL(url);
	contactSoundAudioUrls.clear();
	contactSoundListPromise = null;
	contactSoundOwnerId = Authentication.currentUserId ?? null;
}

async function loadContactSoundMap(force = false): Promise<Map<string, CoretContactNotificationSound>> {
	const ownerId = Authentication.currentUserId ?? null;
	if (ownerId !== contactSoundOwnerId) resetContactSoundCache();
	if (force || !contactSoundListPromise) {
		contactSoundListPromise = request<CoretContactNotificationSoundsResponse>('/notification-sounds')
			.then((response) => new Map(response.sounds.map((entry) => [entry.contact_id, entry])))
			.catch((error) => {
				contactSoundListPromise = null;
				throw error;
			});
	}
	return contactSoundListPromise;
}

export async function getCoretContactNotificationSound(
	contactId: string,
): Promise<CoretContactNotificationSound | null> {
	return (await loadContactSoundMap()).get(contactId) ?? null;
}

export async function setCoretContactNotificationSound(
	contactId: string,
	file: File,
): Promise<CoretContactNotificationSound> {
	if (file.size === 0 || file.size > 3 * 1024 * 1024) throw new Error('O som deve ter no máximo 3 MB.');
	const audioData = await fileToDataUrl(file);
	await request<CoretContactNotificationSoundsResponse>(`/notification-sounds/${encodeURIComponent(contactId)}`, {
		method: 'PUT',
		body: JSON.stringify({audio_data: audioData, file_name: file.name}),
	});
	clearContactSoundAudioUrl(contactId);
	return (await loadContactSoundMap(true)).get(contactId)!;
}

export async function removeCoretContactNotificationSound(contactId: string): Promise<void> {
	await request<CoretContactNotificationSoundsResponse>(
		`/notification-sounds/${encodeURIComponent(contactId)}`,
		{method: 'DELETE'},
	);
	clearContactSoundAudioUrl(contactId);
	await loadContactSoundMap(true);
}

async function getContactSoundAudioUrl(contactId: string): Promise<string | null> {
	const cached = contactSoundAudioUrls.get(contactId);
	if (cached) return cached;
	const token = Authentication.authToken;
	if (!token) return null;
	const response = await fetch(
		`${API_BASE}/notification-sounds/${encodeURIComponent(contactId)}/audio`,
		{headers: {Authorization: token}, cache: 'no-store'},
	);
	if (!response.ok) return null;
	const blob = await response.blob();
	if (!blob.size) return null;
	const url = URL.createObjectURL(blob);
	contactSoundAudioUrls.set(contactId, url);
	return url;
}

export async function playCoretContactNotificationSound(contactId: string): Promise<boolean> {
	const configured = await loadContactSoundMap().then((sounds) => sounds.has(contactId)).catch(() => false);
	if (!configured) return false;
	const url = await getContactSoundAudioUrl(contactId).catch(() => null);
	if (!url) return false;
	try {
		const audio = new Audio(url);
		audio.volume = 1;
		await audio.play();
		return true;
	} catch {
		return false;
	}
}

export function fileToDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(reader.error ?? new Error('Não foi possível ler a imagem.'));
		reader.readAsDataURL(file);
	});
}
